/**
 * HTTP Client - Core axios instance with interceptors
 * Enterprise-grade HTTP client with authentication, refresh tokens, and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse, TokenResponse } from '../types';

// ============================================
// Configuration
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

// ============================================
// Token Storage (in-memory + sessionStorage for access token)
// Access token is persisted to sessionStorage so it survives page refresh; refresh token stays in httpOnly cookie.
// ============================================

const ACCESS_TOKEN_STORAGE_KEY = 'convpilot_access_token';
const IMPERSONATION_BACKUP_KEY = 'convpilot_impersonation_backup';
const ANALYST_IMPERSONATION_BACKUP_KEY = 'convpilot_analyst_impersonation_backup';

let memoryAccessToken: string | null = null;
let memoryRefreshToken: string | null = null;
let memoryImpersonationBackup: { accessToken: string; refreshToken: string } | null = null;
let memoryAnalystImpersonationBackup: { accessToken: string; refreshToken: string } | null = null;

function readImpersonationBackup(key: string): { accessToken: string; refreshToken: string } | null {
  if (key === IMPERSONATION_BACKUP_KEY && memoryImpersonationBackup) return memoryImpersonationBackup;
  if (key === ANALYST_IMPERSONATION_BACKUP_KEY && memoryAnalystImpersonationBackup) return memoryAnalystImpersonationBackup;
  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as { accessToken?: string; refreshToken?: string };
        if (parsed?.accessToken) {
          const backup = { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken ?? '' };
          if (key === IMPERSONATION_BACKUP_KEY) memoryImpersonationBackup = backup;
          else memoryAnalystImpersonationBackup = backup;
          return backup;
        }
      }
    } catch {}
  }
  return null;
}

function readAccessTokenFromStorage(): string | null {
  if (memoryAccessToken) return memoryAccessToken;
  if (typeof sessionStorage !== 'undefined') {
    const stored = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (stored) {
      memoryAccessToken = stored;
      return stored;
    }
  }
  return null;
}

export const tokenStorage = {
  getAccessToken: (): string | null => readAccessTokenFromStorage(),
  getRefreshToken: (): string | null => memoryRefreshToken,

  setTokens: (accessToken: string, refreshToken: string): void => {
    memoryAccessToken = accessToken;
    memoryRefreshToken = refreshToken;
    if (typeof sessionStorage !== 'undefined') {
      if (accessToken) {
        sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
      } else {
        sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      }
    }
  },

  clearTokens: (): void => {
    memoryAccessToken = null;
    memoryRefreshToken = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }
  },

  hasTokens: (): boolean => {
    return !!readAccessTokenFromStorage();
  },

  setImpersonationBackup: (accessToken: string, refreshToken: string): void => {
    const backup = { accessToken, refreshToken };
    memoryImpersonationBackup = backup;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(IMPERSONATION_BACKUP_KEY, JSON.stringify(backup));
    }
  },
  getImpersonationBackup: (): { accessToken: string; refreshToken: string } | null => {
    return readImpersonationBackup(IMPERSONATION_BACKUP_KEY);
  },
  clearImpersonationBackup: (): void => {
    memoryImpersonationBackup = null;
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
  },
  hasImpersonationBackup: (): boolean => {
    return !!readImpersonationBackup(IMPERSONATION_BACKUP_KEY);
  },

  setAnalystImpersonationBackup: (accessToken: string, refreshToken: string): void => {
    const backup = { accessToken, refreshToken };
    memoryAnalystImpersonationBackup = backup;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(ANALYST_IMPERSONATION_BACKUP_KEY, JSON.stringify(backup));
    }
  },
  getAnalystImpersonationBackup: (): { accessToken: string; refreshToken: string } | null => {
    return readImpersonationBackup(ANALYST_IMPERSONATION_BACKUP_KEY);
  },
  clearAnalystImpersonationBackup: (): void => {
    memoryAnalystImpersonationBackup = null;
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(ANALYST_IMPERSONATION_BACKUP_KEY);
  },
  hasAnalystImpersonationBackup: (): boolean => {
    return !!readImpersonationBackup(ANALYST_IMPERSONATION_BACKUP_KEY);
  },
};

// ============================================
// HTTP Client Instance
// ============================================

const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // send/receive httpOnly cookies (e.g. refresh token)
  paramsSerializer: (params) => {
    // Use encodeURIComponent (%20 for spaces) instead of Axios default (+).
    // This keeps URLs consistent with backend cache keys.
    const parts: string[] = [];
    for (const [key, val] of Object.entries(params)) {
      if (val == null) continue;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
    }
    return parts.join('&');
  },
});

// ============================================
// Token Refresh Queue & Proactive Refresh
// ============================================

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

/** Decode JWT payload without verifying (to read exp). Returns null if invalid. */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return payload;
  } catch {
    return null;
  }
}

/** True if access token is expired or expires within marginSeconds. */
function isAccessTokenExpiredOrExpiring(marginSeconds: number = 120): boolean {
  const token = tokenStorage.getAccessToken();
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (!payload || payload.exp == null) return true;
  return payload.exp * 1000 <= Date.now() + marginSeconds * 1000;
}

/**
 * Perform token refresh using httpOnly cookie (no body).
 * Backend reads refresh token from cookie and returns new accessToken; new refresh is set in cookie.
 * Reuses in-flight refresh.
 */
async function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await axios.post<ApiResponse<TokenResponse>>(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const body = response.data;
      const data = (body && typeof body === 'object' && 'data' in body ? (body as ApiResponse<TokenResponse>).data : body) as TokenResponse;
      const accessToken = data?.accessToken;
      if (!accessToken) {
        throw new Error('Invalid refresh response');
      }
      // Store only access token; refresh is in httpOnly cookie
      tokenStorage.setTokens(accessToken, '');
      return accessToken;
    } catch (err: any) {
      // 400 or 401: refresh token missing/invalid – clear tokens so we don't retry in a loop
      const status = err?.response?.status;
      if (status === 400 || status === 401) {
        tokenStorage.clearTokens();
      }
      throw err;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ============================================
// Request Interceptor
// ============================================

httpClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url ?? '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
      return config;
    }

    let token = tokenStorage.getAccessToken();
    // No token in memory: try refresh from httpOnly cookie (e.g. after reload)
    if (!token) {
      try {
        token = await doRefresh();
      } catch {
        return config;
      }
    }
    // Token expiring soon: refresh using cookie
    if (token && isAccessTokenExpiredOrExpiring(120)) {
      try {
        token = await doRefresh();
      } catch {
        return config;
      }
    }
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// ============================================
// Response Interceptor
// ============================================

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return httpClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const accessToken = await doRefresh();
        processQueue(null, accessToken);
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return httpClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        tokenStorage.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// Response Extractors
// ============================================

export const extractData = <T>(response: { data: ApiResponse<T> }): T => {
  if (response.data && response.data.success) {
    return response.data.data;
  }
  throw new Error('API request failed');
};

export const extractPaginatedData = <T>(response: { data: ApiResponse<T[]> & { meta?: any } }): {
  data: T[];
  total: number;
  page: number;
  limit: number;
} => {
  const rawData = response.data;
  return {
    data: rawData.data,
    total: rawData.meta?.total || 0,
    page: rawData.meta?.page || 1,
    limit: rawData.meta?.limit || 10,
  };
};

export default httpClient;
